import { Component, OnInit, Input, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { DropDownModalComponent } from './drop-down-modal/drop-down-modal.component';
import { CdkOverlayOrigin, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { MyProfileComponent } from '../../consumer-profile/my-profile/my-profile.component';
import { SubmittedApplicationListingComponent } from '../../consumer-profile/submitted-application-listing/submitted-application-listing.component';
import { ApplicationStatusListingComponent } from '../../consumer-profile/application-status-listing/application-status-listing.component';
import { USER_ROLE, UserInfo, LOGIN_INFO_REDUCER_NAME, UserInfoStateStructure, MemberInfo } from '@ptl/account-management';
import { UserInfoService } from 'src/app/angular-elements/shared/services/user-info.service';
import { PostLoginService } from '../../shared/services/post-login.service';
import { Subscription } from 'rxjs';
import { PtlWindowLocationService } from '@ptl/global-components';
import { CommonLoginService } from '../../../shared/services/common-login.service';
import { ProfileViewSelection } from '../../shared/interfaces/profile-view-selection.interface';

@Component({
    selector: 'app-post-login-view',
    templateUrl: './post-login-view.component.html',
    styleUrls: ['./post-login-view.component.scss'],
    standalone: false
})
export class PostLoginViewComponent implements OnInit, OnDestroy {
  @Input() myAccountUrl: string;
  @Input() consumerLogoutRedirectUrl: string;
  @Input() producerLogoutRedirectUrl: string;

  @ViewChild(CdkOverlayOrigin, { static: true }) overlayorigin: CdkOverlayOrigin;
  /** Actual dropdown */
  dropdownElement: OverlayRef;

  @ViewChild('expandableLink', { static: true }) expandableLink: HTMLElement;

  expandedPanelSubscription: Subscription;

  displayName: string;
  loggedInUser: UserInfo;
  isExpanded: boolean;
  loggedInMember: MemberInfo;

  constructor(
    private reducerUtilityService: ReducerUtilityService,
    private overlay: Overlay,
    private postLoginService: PostLoginService,
    private userInfoService: UserInfoService,
    private commonLoginService: CommonLoginService,
    private windowLocationService: PtlWindowLocationService
  ) { }

  ngOnInit() {
    /** Get Basic User Info for the Header */
    const loginInfoState: UserInfoStateStructure
      = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);

    /** Member does not use getUserInfo */
    this.loggedInMember = loginInfoState.memberInfo;
    this.loggedInUser = loginInfoState.userInfo;

    this.displayName = this.loggedInUser?.firstName || this.loggedInMember?.firstName;

    let isIndividualSite: boolean;
    let isMedicareSite: boolean;

    if (window.location.hostname.includes('shop')) {
      isIndividualSite = true;
      isMedicareSite = false;
    } else {
      isIndividualSite = false;
      isMedicareSite = true;
    }

    /** Pass Selection View Data around, configures roles/paths/components for pages */
    let profileViews: ProfileViewSelection[] = [
      {
        label: 'Logout',
        order: 3,
        onSelect: async () => {
          this.dropdownElement?.dispose();
          this.commonLoginService.logout();
        },
        applicableRoles: [
          USER_ROLE.AGENCY_ADMIN,
          USER_ROLE.AGENT,
          USER_ROLE.INTERNAL,
          USER_ROLE.MEMBER,
          USER_ROLE.PLAN,
          USER_ROLE.VISITOR
        ],
        applicableSites: [
          'producer',
          'shop',
          'medicare'
        ]
      },
      {
        label: 'My Account',
        order: 0,
        componentRef: MyProfileComponent,
        onSelect: () => {
          this.windowLocationService.goToUrl(this.myAccountUrl + '?myAccountTab=0');
        },
        applicableRoles: [
          USER_ROLE.AGENCY_ADMIN,
          USER_ROLE.AGENT,
          USER_ROLE.INTERNAL,
          USER_ROLE.MEMBER,
          USER_ROLE.PLAN,
          USER_ROLE.VISITOR
        ],
        applicableSites: [
          'producer',
          'shop',
          'medicare'
        ]
      }
    ]

    if (isMedicareSite) {
      profileViews = profileViews.concat([
        {
          label: 'Check Application Status',
          order: 1,
          componentRef: ApplicationStatusListingComponent,
          onSelect: () => {
            this.windowLocationService.goToUrl(this.myAccountUrl + '?myAccountTab=1');
          },
          applicableRoles: [
            USER_ROLE.MEMBER,
            USER_ROLE.VISITOR
          ],
          applicableSites: [
            'medicare'
          ]
        },
        {
          label: 'View Saved Applications',
          order: 2,
          componentRef: SubmittedApplicationListingComponent,
          onSelect: () => {
            this.windowLocationService.goToUrl(this.myAccountUrl + '?myAccountTab=2');
          },
          applicableRoles: [
            USER_ROLE.MEMBER,
            USER_ROLE.VISITOR
          ],
          applicableSites: [
            'medicare'
          ]
        }]
      )
    };

    this.postLoginService.setProfileViewSelection(profileViews);

    this.userInfoService.setLogoutRedirectUrls(this.consumerLogoutRedirectUrl, this.producerLogoutRedirectUrl);
  }

  ngOnDestroy() {
    if (this.expandedPanelSubscription) {
      this.expandedPanelSubscription.unsubscribe();
    }
  }


  tabIntoPanel(e: KeyboardEvent) {
    // if (this.isExpanded && e.code === 'Tab') {
    // }
  }

  expandPanel(event: Event) {
    this.dropdownElement = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.overlayorigin.elementRef.nativeElement)
        .withPush(true)
        .withDefaultOffsetX(0)
        .withDefaultOffsetY((this.overlayorigin.elementRef.nativeElement as HTMLElement).clientHeight)
        .withPositions([
          { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'top' }
        ]),
      disposeOnNavigation: true,
      hasBackdrop: true,
      backdropClass: ['drop-down-modal-background'],
      scrollStrategy: this.overlay.scrollStrategies.close(),
      panelClass: []
    });

    const userProfilePortal = new ComponentPortal(
      DropDownModalComponent
    );

    // (userProfilePortal.component as unknown as DropDownModalComponent).focusFirstItem();

    this.dropdownElement.attach(userProfilePortal);

    /** If you click outside of the dropdown, hide it */
    this.dropdownElement.backdropClick().subscribe(_ => {
      this.isExpanded = false;
      this.dropdownElement.dispose();
    });

    this.expandedPanelSubscription
      = this.dropdownElement.detachments().subscribe(() => this.isExpanded = false);

    this.isExpanded = true;
  }

}
